import { useEffect, useState } from "react";
import { api, formatApiError, mediaUrl } from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "../components/ImagePicker";
import RichTextEditor from "../components/RichTextEditor";

const empty = {
  title: "",
  excerpt: "",
  body: "",
  featured_image: "",
  author: "",
  status: "published",
};

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/news");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setOpen(true); };
  const startEdit = (it) => { setForm({ ...empty, ...it }); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/api/admin/news/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        body: form.body,
        featured_image: form.featured_image,
        author: form.author,
        status: form.status,
      };
      if (form.id) {
        const { data } = await api.put(`/api/admin/news/${form.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { data } = await api.post("/api/admin/news", payload);
        setItems((arr) => [data, ...arr]);
      }
      setOpen(false);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold">News & Announcements</h1>
          <p className="text-sm text-muted-foreground mt-1">Write posts that appear on the News page.</p>
        </div>
        <Button onClick={startNew} className="bg-primary hover:bg-primary-dark text-white" data-testid="new-news-btn">
          <Plus className="w-4 h-4 mr-2" /> New post
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">No posts yet.</Card>
      ) : (
        <Card className="divide-y">
          {items.map((it) => (
            <div key={it.id} className="p-4 flex items-center gap-4" data-testid={`news-row-${it.id}`}>
              {it.featured_image ? (
                <img src={mediaUrl(it.featured_image)} alt="" className="w-16 h-16 object-cover rounded-md" />
              ) : (
                <div className="w-16 h-16 bg-muted rounded-md" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{it.title}</div>
                <div className="text-xs text-muted-foreground truncate">
                  by {it.author || "Unknown"} · {new Date(it.created_at).toLocaleDateString()}
                </div>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${it.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                {it.status}
              </span>
              <Button size="sm" variant="outline" onClick={() => startEdit(it)} data-testid={`edit-news-${it.id}`}>
                <Pencil className="w-3 h-3" />
              </Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(it.id)} data-testid={`delete-news-${it.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" />
            </div>
            <div>
              <Label>Author</Label>
              <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <Label>Excerpt (1-2 sentences shown on listings)</Label>
              <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </div>
            <ImagePicker label="Featured image" value={form.featured_image} onChange={(v) => setForm({ ...form, featured_image: v })} testId="news-image" />
            <div>
              <Label>Body</Label>
              <RichTextEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} />
            </div>
            <div className="flex items-center justify-between p-3 border rounded-md bg-muted/40">
              <div>
                <Label>Status: {form.status === "published" ? "Published (visible)" : "Draft (hidden)"}</Label>
                <p className="text-xs text-muted-foreground">Toggle to publish or unpublish.</p>
              </div>
              <Switch
                checked={form.status === "published"}
                onCheckedChange={(c) => setForm({ ...form, status: c ? "published" : "draft" })}
                data-testid="news-status-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary-dark text-white" data-testid="save-news-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
